<script setup>
import { ref, computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';
// CUSTOM (Portela 2026-10-21): jornada do agente no próprio cadastro, não só no
// relatório. Mesmo componente, mesma API — uma tabela, duas portas.
import AgentSchedule from 'dashboard/routes/dashboard/settings/reports/components/AgentSchedule.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  email: {
    type: String,
    default: '',
  },
  type: {
    type: String,
    default: '',
  },
  availability: {
    type: String,
    default: '',
  },
  provider: {
    type: String,
    default: '',
  },
  customRoleId: {
    type: Number,
    default: null,
  },
  // CUSTOM (Portela 2026-09-09): permissão POR AGENTE p/ criar macros/respostas rápidas.
  macrosEnabled: {
    type: Boolean,
    default: false,
  },
  cannedResponsesEnabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });
// CUSTOM (Portela 2026-09-09): toggles de permissão de conteúdo por agente.
const agentMacrosEnabled = ref(props.macrosEnabled);
const agentCannedResponsesEnabled = ref(props.cannedResponsesEnabled);
// CUSTOM (Portela 2026-10-22): ref do editor de jornada — o submit deste modal
// grava a jornada também (um botão só), via salvarSeNecessario(id).
const scheduleRef = ref(null);

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

// CUSTOM (Portela 2026-09-09): toggles só fazem sentido p/ o cargo "agente"
// (admin nunca é bloqueado; cargo personalizado tem sua própria política).
const showContentPermissions = computed(
  () => selectedRole.value?.name === 'agent'
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  // Passo 1: salvar o agente. Erro aqui interrompe (não mexe na jornada).
  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
      // CUSTOM (Portela 2026-09-09): permissão POR AGENTE de macros/respostas rápidas.
      macros_enabled: agentMacrosEnabled.value,
      canned_responses_enabled: agentCannedResponsesEnabled.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
    return;
  }

  // Passo 2 (Portela 2026-10-22): o submit do modal salva a jornada também — um
  // botão só. `salvarSeNecessario` só grava se o usuário mexeu na jornada (ver
  // precisaSalvar): editar só o nome NÃO toca na jornada. Se falhar com o agente
  // já salvo, avisa com todas as letras — não desfaz o agente por causa disso.
  try {
    await scheduleRef.value.salvarSeNecessario(props.id);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (erroJornada) {
    useAlert(t('AGENT_MGMT.EDIT.API.SCHEDULE_FAILED'));
  }
  emit('close');
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <!-- CUSTOM (Portela 2026-09-09): permissão POR AGENTE p/ criar macros e
           respostas rápidas. Desligado por padrão (agente bloqueado). Só p/ o
           cargo "agente"; admin nunca é bloqueado. -->
      <div v-if="showContentPermissions" class="w-full flex flex-col gap-3 py-2">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AGENT_MGMT.EDIT.FORM.CONTENT_PERMISSIONS.LABEL') }}
        </span>
        <div class="flex items-center justify-between gap-3">
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.EDIT.FORM.CONTENT_PERMISSIONS.MACROS') }}
          </span>
          <Switch v-model="agentMacrosEnabled" />
        </div>
        <div class="flex items-center justify-between gap-3">
          <span class="text-body-main text-n-slate-11">
            {{ $t('AGENT_MGMT.EDIT.FORM.CONTENT_PERMISSIONS.CANNED') }}
          </span>
          <Switch v-model="agentCannedResponsesEnabled" />
        </div>
      </div>

      <!-- CUSTOM (Portela 2026-10-21/22): jornada do agente. `mostrar-botao=false`
           esconde o botão próprio — quem grava é o submit "Editar agente" (um botão
           só). Recebe o id; sem jornada abre com a semana FECHADA. -->
      <AgentSchedule
        ref="scheduleRef"
        :user-id="props.id"
        :mostrar-botao="false"
      />

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>
